import { ROOF_SNAPS } from '../components/DesignRoofViewport';
import { GetShadeCol } from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/RefreshShade';
import type { PreContractModuleCell, PreContractRoofModuleLayout } from './preContractTypes';

/** True when solar access has not arrived from oneDRAW shade yet (not a real 0% SA). */
export function isPreContractShadePending(solarAccess: number | undefined): boolean {
    return !Number.isFinite(solarAccess) || (solarAccess as number) <= 0;
}

/** Panels at or below this solar access count as low efficiency (heavily shaded). */
export const PRE_CONTRACT_LOW_EFFICIENCY_SA = 50;
/** Purple panels: below this solar access (matches GetShadeCol). */
export const PRE_CONTRACT_PURPLE_SA_MAX = 30;
/** Grey panels: 30% up through low-efficiency cutoff. */
export const PRE_CONTRACT_GREY_SA_MIN = 30;

/**
 * True once shade has arrived and the panel is purple (<30% SA).
 */
export function isPreContractPurplePanel(solarAccess: number | undefined): boolean {
    if (isPreContractShadePending(solarAccess)) return false;
    return (solarAccess as number) < PRE_CONTRACT_PURPLE_SA_MAX;
}

/**
 * True once shade has arrived and the panel is grey (30–50% SA).
 */
export function isPreContractGreyPanel(solarAccess: number | undefined): boolean {
    if (isPreContractShadePending(solarAccess)) return false;
    const sa = solarAccess as number;
    return sa >= PRE_CONTRACT_GREY_SA_MIN && sa <= PRE_CONTRACT_LOW_EFFICIENCY_SA;
}

/**
 * True once shade has arrived and the panel is grey or purple low-efficiency.
 */
export function isPreContractLowEfficiency(solarAccess: number | undefined): boolean {
    return isPreContractPurplePanel(solarAccess) || isPreContractGreyPanel(solarAccess);
}

/** Whether a module may be selected given grey/purple intake toggles. */
export function isPreContractPanelAllowed(
    solarAccess: number | undefined,
    includeGreyPanels: boolean,
    includePurplePanels: boolean,
): boolean {
    if (isPreContractShadePending(solarAccess)) return true;
    if (isPreContractPurplePanel(solarAccess)) return includePurplePanels;
    if (isPreContractGreyPanel(solarAccess)) return includeGreyPanels;
    return true;
}

/** Same thresholds as editor `GetShadeCol` (including purple-modules flag). */
export function getPreContractShadeColor(val: number): string {
    if (isPreContractShadePending(val)) return '#0c4a6e';
    return GetShadeCol(val, true);
}

/** Convert oneDRAW irrColor (0xRRGGBB) to a CSS hex string. */
export function preContractIrrColorToCss(irrColor: number | undefined): string | null {
    if (!Number.isFinite(irrColor) || (irrColor as number) <= 0) return null;
    return `#${((irrColor as number) & 0xffffff).toString(16).padStart(6, '0')}`;
}

/** Panel fill color — prefers live irrColor from the 3D mesh when synced. */
export function getPreContractModuleShadeColor(
    cell: Exclude<PreContractModuleCell, null> | null | undefined,
): string {
    if (!cell) return '#0c4a6e';
    if (cell.shadeColor) return cell.shadeColor;
    return getPreContractShadeColor(cell.solarAccess);
}

/**
 * Layout maps traced from the site aerial module arrays (max-fill).
 * `1` = module present, `0` = empty gap in the roof face grid.
 */
type FaceBlueprint = {
    cols: number;
    /** Row-major presence map */
    map: number[][];
    /** Solar access samples */
    sa: number[];
    /** Which present modules start included (same order as map walk) */
    included?: boolean[];
    /** Module rectangle orientation on this face */
    moduleOrientation: 'portrait' | 'landscape';
};

const FACE_BLUEPRINTS: FaceBlueprint[] = [
    // Roof 1 — main south array: portrait modules in two rows
    {
        cols: 11,
        map: [
            [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
            [1, 1, 1, 1, 1, 0, 1, 1, 1, 1, 1],
        ],
        sa: [62, 64, 66, 68, 70, 69, 67, 65, 63, 61, 60, 62, 64, 66, 68, 58, 60, 62, 64, 66],
        included: Array(20).fill(true),
        moduleOrientation: 'portrait',
    },
    // Roof 2 — west bay
    {
        cols: 3,
        map: [[1, 1, 1]],
        sa: [55, 58, 52],
        included: [true, true, true],
        moduleOrientation: 'portrait',
    },
    // Roof 3 — NW upper plane
    {
        cols: 4,
        map: [[1, 1, 1, 1]],
        sa: [72, 74, 70, 68],
        included: [true, true, true, true],
        moduleOrientation: 'landscape',
    },
    // Roof 4 — NE upper plane max fill
    {
        cols: 3,
        map: [
            [1, 1, 0],
            [1, 1, 1],
        ],
        sa: [64, 58, 52, 48, 45],
        included: Array(5).fill(true),
        moduleOrientation: 'landscape',
    },
];

function buildFaceModules(
    roofTagId: number,
    blueprint: FaceBlueprint,
    roofSelected: boolean,
    maxFill: boolean,
): { cols: number; modules: PreContractModuleCell[]; moduleOrientation: 'portrait' | 'landscape' } {
    const modules: PreContractModuleCell[] = [];
    let presentIndex = 0;

    blueprint.map.forEach((row) => {
        row.forEach((cell) => {
            if (!cell) {
                modules.push(null);
                return;
            }
            const solarAccess = blueprint.sa[presentIndex] ?? 60;
            const includedDefault = maxFill
                ? true
                : (blueprint.included?.[presentIndex] ?? true);
            modules.push({
                id: `R${roofTagId}M${presentIndex + 1}`,
                solarAccess,
                included: roofSelected && includedDefault,
            });
            presentIndex += 1;
        });
    });

    return { cols: blueprint.cols, modules, moduleOrientation: blueprint.moduleOrientation };
}

export type RoofLayoutSource = {
    roofTagId: number;
    label: string;
};

/**
 * Build module boards aligned to the Design drawer roof list (live site roofs when available).
 * Falls back to demo ROOF_SNAPS when no site roofs are provided yet.
 */
export function buildRoofModuleLayouts(
    roofSelections: boolean[],
    options?: { maxFill?: boolean; roofs?: RoofLayoutSource[] },
): PreContractRoofModuleLayout[] {
    const maxFill = options?.maxFill !== false;
    const roofs: RoofLayoutSource[] =
        options?.roofs && options.roofs.length > 0
            ? options.roofs
            : ROOF_SNAPS.map((snap, index) => ({
                  roofTagId: index + 1,
                  label: snap.label,
              }));

    return roofs.map((roof, roofIndex) => {
        const blueprint = FACE_BLUEPRINTS[roofIndex % FACE_BLUEPRINTS.length] ?? FACE_BLUEPRINTS[0];
        const roofSelected = roofSelections[roofIndex] !== false;
        const built = buildFaceModules(roof.roofTagId, blueprint, roofSelected, maxFill);
        return {
            roofIndex,
            label: roof.label,
            cols: built.cols,
            modules: built.modules,
            moduleOrientation: built.moduleOrientation,
        };
    });
}

/** @deprecated Prefer buildRoofModuleLayouts — kept for call-site compatibility. */
export function buildDemoRoofModuleLayouts(
    roofSelections: boolean[],
    options?: { maxFill?: boolean; roofs?: RoofLayoutSource[] },
): PreContractRoofModuleLayout[] {
    return buildRoofModuleLayouts(roofSelections, options);
}

/** All facets selected — used for the default max-fill module layout. */
export function maxFillRoofSelections(roofCount?: number): boolean[] {
    const count = roofCount && roofCount > 0 ? roofCount : ROOF_SNAPS.length;
    return Array.from({ length: count }, () => true);
}

/** Rough MA coastal specific yield (kWh / kW-DC / year) for pre-contract sizing checks. */
const PRECONTRACT_SPECIFIC_YIELD_KWH_PER_KW = 1250;

export function countIncludedModules(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): { count: number; averageSolarAccess: number } {
    let count = 0;
    let saSum = 0;
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.included) return;
            count += 1;
            saSum += cell.solarAccess;
        });
    });
    return {
        count,
        averageSolarAccess: count > 0 ? saSum / count : 0,
    };
}

/** Estimate annual production (kWh) from active modules, wattage, and average SA. */
export function estimateLayoutAnnualKwh(args: {
    layouts: PreContractRoofModuleLayout[];
    roofSelections: boolean[];
    moduleWattage: number | null;
}): number | null {
    const wattage = args.moduleWattage;
    if (wattage == null || !Number.isFinite(wattage) || wattage <= 0) return null;
    const { count, averageSolarAccess } = countIncludedModules(args.layouts, args.roofSelections);
    if (count === 0) return 0;
    const systemKw = (count * wattage) / 1000;
    const saFactor =
        Number.isFinite(averageSolarAccess) && averageSolarAccess > 0
            ? Math.min(averageSolarAccess, 100) / 100
            : 1;
    return Math.round(systemKw * PRECONTRACT_SPECIFIC_YIELD_KWH_PER_KW * saFactor);
}

/** oneDRAW max-fill on active roofs: prefer live module kWh, else SA-weighted estimate. */
export function estimateMaxfillAnnualKwh(args: {
    layouts: PreContractRoofModuleLayout[];
    roofSelections: boolean[];
    moduleWattage: number | null;
}): {
    kwh: number | null;
    moduleCount: number;
    usedLiveProduction: boolean;
    waitingForShade: boolean;
} {
    let present = 0;
    let liveSum = 0;
    let liveCount = 0;
    let saSum = 0;
    args.layouts.forEach((layout) => {
        if (args.roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell) return;
            present += 1;
            saSum += cell.solarAccess;
            if (typeof cell.productionKwh === 'number' && cell.productionKwh > 0) {
                liveSum += cell.productionKwh;
                liveCount += 1;
            }
        });
    });

    if (present === 0) {
        return { kwh: 0, moduleCount: 0, usedLiveProduction: false, waitingForShade: false };
    }

    if (liveCount === present) {
        return {
            kwh: Math.round(liveSum),
            moduleCount: present,
            usedLiveProduction: true,
            waitingForShade: false,
        };
    }

    const avgSa = saSum / present;
    const waitingForShade = avgSa <= 0;
    if (waitingForShade) {
        return {
            kwh: null,
            moduleCount: present,
            usedLiveProduction: false,
            waitingForShade: true,
        };
    }

    const maxfillLayouts = args.layouts.map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => (cell ? { ...cell, included: true } : null)),
    }));
    const estimated = estimateLayoutAnnualKwh({
        layouts: maxfillLayouts,
        roofSelections: args.roofSelections,
        moduleWattage: args.moduleWattage,
    });
    return {
        kwh: estimated,
        moduleCount: present,
        usedLiveProduction: false,
        waitingForShade: false,
    };
}

export function estimateTreeCount(salesForceId: string): number {
    // Site preview shows substantial canopy on both sides of the house.
    let hash = 0;
    for (let i = 0; i < salesForceId.length; i += 1) {
        hash = (hash + salesForceId.charCodeAt(i) * (i + 3)) % 11;
    }
    return 8 + (hash % 5);
}
